import React from 'react';
import styled from 'styled-components';
import {
  PageListingFiltersFragment,
  PropertiesListingConfigFragment,
  PropertyCardFragment,
  PropertySpecFragment,
} from '../../../gatsby-graphql';
import { BaseContainer } from '../BaseComponents';
import PropertiesListing from '../PropertiesListing/PropertiesListing';
import PropertiesFilters from '../PropertiesFilters/PropertiesFilters';
import usePropertiesFilters from '../../hooks/usePropertiesFilters';
import PropertiesFiltersContext from '../../context/PropertiesFiltersContext';
import ListTypeToggle from '../ListTypeToggle/ListTypeToggle';
import FilterSelect from '../FilterSelect/FilterSelect';
import Button from '../Button/Button';

interface IPropertiesListingDiscoveryProps {
  properties: PropertyCardFragment[];
  propertySpecs: PropertySpecFragment[];
  propertiesCountLabel?: string;
  propertyTypeName?: string;
  operationTypeListingName?: string;
  propertyLocationName?: string;
  totalProperties: number;
  propertyLocations: PageListingFiltersFragment['propertyLocations'];
  propertiesListingConfig: PropertiesListingConfigFragment;
  activePropertyType?: { label: string; value: string };
  activePropertyLocationType?: { label: string; value: string };
  localizedFilters?: boolean;
}

const PropertiesListingDiscovery: React.FC<IPropertiesListingDiscoveryProps> = ({
  properties,
  propertiesCountLabel,
  propertyTypeName,
  operationTypeListingName,
  propertyLocationName,
  propertiesListingConfig,
  propertyLocations,
  activePropertyType,
  activePropertyLocationType,
  localizedFilters,
  propertySpecs,
}) => {
  const propertiesFilters = usePropertiesFilters(properties);
  const propertiesLength = propertiesFilters.filterPropertiesLength;
  const heading = propertiesCountLabel
    ? `${propertiesLength} ${propertiesCountLabel}`
    : `${propertiesLength} ${
        propertiesLength > 1 ? `${propertyTypeName}s` : `${propertyTypeName}`
      } ${operationTypeListingName} ${
        propertyLocationName ? `en ${propertyLocationName}` : ''
      }`;
  return (
    <PropertiesFiltersContext.Provider value={propertiesFilters}>
      <Container>
        <div>
          <PropertiesFilters
            {...{
              propertyLocations,
              activePropertyType,
              activePropertyLocationType,
              localizedFilters,
              propertySpecs,
            }}
            config={propertiesListingConfig}
          />
        </div>
        <div>
          <HeadingContainer>
            <h1>{heading}</h1>
            <HeadingControls>
              <div>
                <FilterSelect
                  options={propertiesFilters.listingOrderList}
                  onChange={(option) =>
                    propertiesFilters.setListingOrder(option?.value)
                  }
                  defaultValue={propertiesFilters.listingOrderList[0]}
                />
              </div>
              <Button
                onClick={() =>
                  propertiesFilters.setIsDrawerOpen((prevValue) => !prevValue)
                }
                label={propertiesListingConfig.mobileTitle as string}
                color="primary"
                buttonStyle="solid"
              />
              <ListTypeToggle />
            </HeadingControls>
          </HeadingContainer>
          {propertiesLength > 0 && (
            <PropertiesListing
              imageResolver="listingGrid"
              listingType={propertiesFilters.listingType}
              properties={propertiesFilters.filterProperties}
              internal
            />
          )}
        </div>
      </Container>
    </PropertiesFiltersContext.Provider>
  );
};

const Container = styled(BaseContainer)`
  display: grid;
  margin-top: 32px;
  ${(props) => props.theme.mq.tablet} {
  }
  ${(props) => props.theme.mq.desktop} {
    margin-top: 78px;
  }
  ${(props) => props.theme.mq.desktop} {
    gap: 34px;
    grid-template-columns: 3fr 9fr;
  }
  h1 {
    font-size: 22px;
    line-height: 26px;
    font-weight: bold;
    margin: 0;
  }
`;

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 25px;
  ${(props) => props.theme.mq.untilDesktop} {
    flex-wrap: wrap;
    h1:first-of-type {
      flex-basis: 100%;
      margin-bottom: 10px;
    }
  }
`;

const HeadingControls = styled.div`
  display: flex;
  align-items: center;
  & > button:first-of-type {
    padding: 8.5px 30px;
    font-size: 14px;
    line-height: 17px;
  }
  ${(props) => props.theme.mq.untilDesktop} {
    flex-basis: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 26px;
  }
  ${(props) => props.theme.mq.desktop} {
    & > div:first-of-type {
      margin-right: 24px;
      width: 130px;
    }
    & > button:first-of-type {
      display: none;
    }
  }
`;

PropertiesListingDiscovery.defaultProps = {
  propertyTypeName: '',
  operationTypeListingName: '',
  propertyLocationName: '',
};

export default PropertiesListingDiscovery;
